/**
 * Decision 0146 (memory phase 2, 0140 §16): private per-person memory. A person's own claims ("I review PRs on my
 * phone", "my defaults for new projects: Cloudflare, React, TanStack"), stored per person in the WorkspaceDO, never in a
 * project log, never in a team index or export. Readable by that person (Account settings, their own Claude Code /
 * Codex / dot over the person MCP) and their own agents in their own turns in a project only they are in.
 *
 * Refs are `PM-n`, numbered per person. Superseded, never silently replaced: a claim that changed keeps the old one
 * (`supersededBy`, `validTo`); edits keep their earlier revisions. The person may delete one for good (their data).
 */
import { Schema } from "effect"
import { Millis } from "./ids"

export const PersonalMemoryKind = Schema.Literals(["preference", "fact", "learning"])
export type PersonalMemoryKind = typeof PersonalMemoryKind.Type

export const PersonalMemoryItem = Schema.Struct({
  id: Schema.String,
  /** PM-3 (per person). */
  ref: Schema.String,
  kind: PersonalMemoryKind,
  title: Schema.String,
  text: Schema.String,
  createdAt: Millis,
  updatedAt: Millis,
  revision: Schema.Int,
  /** Who wrote the current revision, in words ("you", "you via Claude Code", "@lea"). */
  writtenBy: Schema.String,
  /** In effect: not superseded, `validTo` not passed. */
  current: Schema.Boolean,
  supersededBy: Schema.optionalKey(Schema.String),
  supersedes: Schema.optionalKey(Schema.String),
  validFrom: Schema.optionalKey(Millis),
  validTo: Schema.optionalKey(Millis),
  lastConfirmedAt: Schema.optionalKey(Millis),
})
export type PersonalMemoryItem = typeof PersonalMemoryItem.Type

const Text = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(8_000))
const Title = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(160))
const PmRef = Schema.String.check(Schema.isPattern(/^PM-[1-9][0-9]*$/i))

/** A write to one's own private memory (Account settings, the person MCP, a person's own agent). */
export const PersonalMemoryWrite = Schema.Struct({
  kind: Schema.optionalKey(PersonalMemoryKind),
  title: Schema.optionalKey(Title),
  text: Schema.optionalKey(Text),
  /** A new revision of PM-n (an edit; the earlier text is kept as a revision). */
  replaces: Schema.optionalKey(PmRef),
  /** A new claim that replaces PM-n (PM-n is kept, marked superseded). */
  supersedes: Schema.optionalKey(PmRef),
  /** PM-n still holds (sets `lastConfirmedAt`). */
  confirm: Schema.optionalKey(PmRef),
  validFrom: Schema.optionalKey(Millis),
  validTo: Schema.optionalKey(Millis),
})
export type PersonalMemoryWrite = typeof PersonalMemoryWrite.Type

/** GET /api/workspaces/:ws/me/memory */
export const PersonalMemoryList = Schema.Struct({ items: Schema.Array(PersonalMemoryItem) })
export type PersonalMemoryList = typeof PersonalMemoryList.Type
